import React, { useEffect, useMemo, useState } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { toast } from 'react-toastify';
import { getCrmAccountsListOperation } from './operations/get-crm-accounts-list-operation';
import { Button } from 'primereact/button';
import { AddCrmAccountDialog } from './components/add-crm-account-dialog';
import { EditCrmAccountDialog } from './components/edit-crm-account-dialog';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { deleteCrmAccountOperation } from './operations/delete-crm-account-operation';
import { CrmAccountDetailsDialog } from './account-page-dialog/account-page-dialog';
import {
  CrmAccountEntity,
  CrmAccountType,
  CrmVatStatus,
} from '../../../api/types/crm/accounts/crm-account-entity';
import { AccountsFilter, AccountsFilters } from './components/accounts-filter';
import { CrmAccountType as CrmAccountTypeEnum } from '../../../api/types/crm/accounts/crm-account-entity';

const accountTypeLabel = (type: CrmAccountType | null) => {
  switch (type) {
    case CrmAccountType.Company:
      return 'חברה בע״מ';
    case CrmAccountType.Association:
      return 'עמותה';
    case CrmAccountType.NonProfit:
      return 'מלכ״ר / חל״צ';
    case CrmAccountType.SelfEmployed:
      return 'עצמאי';
    case CrmAccountType.Government:
      return 'גוף ממשלתי';
    case CrmAccountType.Institution:
      return 'מוסד';
    case CrmAccountType.Individual:
      return 'פרטי';
    case CrmAccountType.Other:
      return 'אחר';
    default:
      return 'חסר';
  }
};

const vatStatusLabel = (status: CrmVatStatus | null) => {
  switch (status) {
    case CrmVatStatus.VatRegistered:
      return 'עוסק מורשה';
    case CrmVatStatus.VatExempt:
      return 'עוסק פטור';
    default:
      return 'חסר';
  }
};

const optionalTextCell = (value: string | null | undefined) => {
  if (value === null || value === undefined) return 'חסר';
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : 'ריק';
};

export const AccountsPage = () => {
  const [data, setData] = useState<CrmAccountEntity[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [filters, setFilters] = useState<AccountsFilters>({
    accountName: '',
    accountTypes: [],
  });
  const [showAddDialog, setShowAddDialog] = useState<boolean>(false);
  const [showEditDialog, setShowEditDialog] = useState<boolean>(false);
  const [accountToEdit, setAccountToEdit] = useState<CrmAccountEntity | null>(
    null,
  );
  const [showDetailsDialog, setShowDetailsDialog] = useState<boolean>(false);
  const [selectedAccount, setSelectedAccount] =
    useState<CrmAccountEntity | null>(null);

  const [deletingId, setDeletingId] = useState<number | null>(null);

  const [first, setFirst] = useState<number>(0);
  const [rows, setRows] = useState<number>(25);

  const offset = useMemo(() => first, [first]);
  const limit = useMemo(() => rows, [rows]);

  useEffect(() => {
    const effectiveAccountTypes =
      filters.accountTypes && filters.accountTypes.length > 0
        ? filters.accountTypes
        : Object.values(CrmAccountTypeEnum);
    getCrmAccountsListOperation(
      offset,
      limit,
      {
        accountName: filters.accountName,
        accountTypes: effectiveAccountTypes,
      },
      setData,
      setTotal,
      setLoading,
    );
  }, [offset, limit, filters.accountName, filters.accountTypes]);

  const refresh = () =>
    getCrmAccountsListOperation(
      offset,
      limit,
      {
        accountName: filters.accountName,
        accountTypes:
          filters.accountTypes && filters.accountTypes.length > 0
            ? filters.accountTypes
            : Object.values(CrmAccountTypeEnum),
      },
      setData,
      setTotal,
      setLoading,
    );

  const handleDelete = (account: CrmAccountEntity) => {
    if (deletingId !== null) return;

    confirmDialog({
      header: 'אישור מחיקת ארגון',
      icon: 'pi pi-exclamation-triangle',
      message: (
        <div
          style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}
        >
          <div>
            האם אתה בטוח שברצונך למחוק את הארגון{' '}
            <strong>{account.accountName}</strong>?
          </div>
          <div
            style={{ color: '#6b7280', fontSize: '0.95rem', lineHeight: 1.4 }}
          >
            שים לב: ארגון שכבר ביצע תשלומים / יש לו אנשי קשר / תרומות לא ניתן
            למחיקה.
          </div>
        </div>
      ),
      acceptLabel: 'מחיקה',
      rejectLabel: 'ביטול',
      acceptClassName: 'p-button-danger',
      accept: () => {
        deleteCrmAccountOperation(account.id, setDeletingId, refresh);
      },
    });
  };

  return (
    <div
      style={{
        display: 'flex',
        gap: '20px',
        paddingTop: '16px',
        height: 'calc(100vh - 80px)',
        direction: 'rtl',
      }}
    >
      {/* Filter Section */}
      <AccountsFilter
        value={filters}
        onChange={(newFilters) => {
          // reset to first page on filter change
          setFirst(0);
          setFilters(newFilters);
        }}
      />

      {/* Table Section */}
      <div style={{ flex: 1, marginLeft: '16px' }}>
        <ConfirmDialog />
        <div
          style={{
            backgroundColor: '#f0fbf9',
            borderLeft: `4px solid #009688`,
            padding: '20px',
            marginBottom: '16px',
            borderRadius: '8px',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '1rem',
            }}
          >
            <div>
              <h1
                style={{
                  margin: 0,
                  fontSize: '2rem',
                  color: '#00796b',
                  fontWeight: 'bold',
                }}
              >
                חשבונות לקוח
              </h1>
              <p
                style={{
                  margin: '8px 0 0 0',
                  color: '#666',
                  fontSize: '1.05rem',
                }}
              >
                חשבונות לקוח הם הישויות הארגוניות במערכת — מוסדות, חברות או
                גורמים פרטיים המשויכים לתשלומים ותרומות. לכל חשבון מוצמדת רשימת
                אנשי קשר, והצגת החשבון כוללת את היסטוריית התרומות, התשלומים
                והאנשים הקשורים אליו.
              </p>
            </div>

            <Button
              tooltip={'הוספת חשבון לקוח'}
              icon="pi pi-plus"
              style={{ width: '45px', height: '30px' }}
              onClick={() => setShowAddDialog(true)}
            />
          </div>
        </div>

        <div style={{ background: 'white', borderRadius: '8px' }}>
          <DataTable
            value={data}
            loading={loading}
            paginator
            lazy
            first={first}
            rows={rows}
            totalRecords={total}
            onPage={(e) => {
              setFirst(e.first ?? 0);
              setRows(e.rows ?? 25);
            }}
            rowsPerPageOptions={[10, 25, 50, 100]}
            dataKey="id"
            emptyMessage={
              <div style={{ textAlign: 'right' }}>לא נמצאו נתונים</div>
            }
            onRowClick={(e) => {
              const row = e.data as CrmAccountEntity;
              setSelectedAccount(row);
              setShowDetailsDialog(true);
            }}
            style={{ direction: 'rtl' }}
          >
            <Column
              header="פעולות"
              style={{ width: '110px', textAlign: 'right' }}
              body={(row: CrmAccountEntity) => (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    gap: '0.25rem',
                  }}
                >
                  <Button
                    icon="pi pi-pencil"
                    text
                    severity="warning"
                    tooltip="עדכון"
                    onClick={(ev) => {
                      ev.stopPropagation();
                      setAccountToEdit(row);
                      setShowEditDialog(true);
                    }}
                  />
                  <Button
                    icon="pi pi-trash"
                    text
                    severity="danger"
                    tooltip="מחיקה"
                    loading={deletingId === row.id}
                    disabled={deletingId !== null}
                    onClick={(ev) => {
                      ev.stopPropagation();
                      handleDelete(row);
                    }}
                  />
                </div>
              )}
            />
            <Column
              field="id"
              header="מזהה"
              style={{ width: '90px', textAlign: 'right' }}
            />
            <Column
              field="accountName"
              header="שם"
              style={{ textAlign: 'right' }}
            />
            <Column
              header="סוג"
              style={{ textAlign: 'right' }}
              body={(row: CrmAccountEntity) =>
                accountTypeLabel(row.accountType)
              }
            />
            <Column
              style={{ textAlign: 'right' }}
              header="מע״מ"
              body={(row: CrmAccountEntity) => vatStatusLabel(row.vatStatus)}
            />
            <Column
              style={{ textAlign: 'right' }}
              field="registrationNumber"
              header="מס׳ רישום"
              body={(row: CrmAccountEntity) =>
                optionalTextCell(row.registrationNumber)
              }
            />
            <Column
              style={{ textAlign: 'right' }}
              field="phone"
              header="טלפון"
              body={(row: CrmAccountEntity) => optionalTextCell(row.phone)}
            />
            <Column
              style={{ textAlign: 'right' }}
              field="email"
              header="אימייל"
              body={(row: CrmAccountEntity) => optionalTextCell(row.email)}
            />
            <Column
              style={{ textAlign: 'right' }}
              field="address"
              header="כתובת"
              body={(row: CrmAccountEntity) => optionalTextCell(row.address)}
            />
          </DataTable>
        </div>

        <AddCrmAccountDialog
          visible={showAddDialog}
          onHide={() => setShowAddDialog(false)}
          onSuccess={() => {
            // refresh current page
            refresh();
          }}
        />

        <EditCrmAccountDialog
          visible={showEditDialog}
          account={accountToEdit}
          onHide={() => {
            setShowEditDialog(false);
            setAccountToEdit(null);
          }}
          onSuccess={() => {
            refresh();
          }}
        />

        <CrmAccountDetailsDialog
          visible={showDetailsDialog}
          account={selectedAccount}
          onHide={() => {
            setShowDetailsDialog(false);
            setSelectedAccount(null);
          }}
        />
      </div>
    </div>
  );
};
